import type { ActivityRecord } from '../model/activity.js'
import { recalculated, type SummaryValue, unavailable } from './provenance.js'

/**
 * Heart-rate summary recalculation (`summary-calculation` spec). Heart
 * rate is a direct per-record device measurement (bpm, no FIT
 * scale/offset applied) — resummarizing it for a narrowed/retained
 * record window is a `recalculated` value in the same sense
 * `computeMaxSpeedMetersPerSecond` (`calc/distance.ts`) already treats
 * per-record speed: genuine device-measured data, just resummarized for
 * a smaller retained range, never a geometric or proportional guess.
 * There is no GPS-style fallback here (nothing to approximate HR from
 * other fields) — either retained records carry `heartRate` samples or
 * the value is `unavailable`, never fabricated.
 */

/**
 * FIT `heart_rate` is a uint8 whose invalid sentinel is 255; the official
 * SDK normally drops invalid values on decode, but a value of 0 (strap
 * lost contact) or 255 surviving through an edit/merge path must not
 * drag the average down or become the max. Only finite values in
 * (0, 255) count as samples.
 */
function isValidHeartRate(hr: unknown): hr is number {
  return typeof hr === 'number' && Number.isFinite(hr) && hr > 0 && hr < 255
}

function heartRateSamples(records: ActivityRecord[]): number[] {
  const samples: number[] = []
  for (const r of records) if (isValidHeartRate(r.heartRate)) samples.push(r.heartRate)
  return samples
}

export function computeAverageHeartRate(records: ActivityRecord[]): SummaryValue<number> {
  const samples = heartRateSamples(records)
  if (samples.length === 0) return unavailable()
  const avg = samples.reduce((sum, hr) => sum + hr, 0) / samples.length
  return recalculated(Math.round(avg))
}

export function computeMaxHeartRate(records: ActivityRecord[]): SummaryValue<number> {
  const samples = heartRateSamples(records)
  if (samples.length === 0) return unavailable()
  let max = 0
  for (const hr of samples) if (hr > max) max = hr
  return recalculated(max)
}
